import React, { useEffect, useRef, useState } from "react";

import {
  BarsMiniIcon,
  BellIcon,
  BoltMiniIcon,
  InfoIcon,
  PulseMiniIcon,
} from "../../lib/icons";
import { IndofastLogo } from "./IndofastLogo";

const ICONS = {
  bolt: BoltMiniIcon,
  bars: BarsMiniIcon,
  pulse: PulseMiniIcon,
};

const AGENT_STATUS_OPTIONS = [
  { value: "online", label: "Online" },
  { value: "short_break", label: "Short Break" },
  { value: "lunch", label: "Lunch" },
  { value: "training", label: "Training" },
];

function Topbar({
  activeTopNav,
  currentUser,
  navItems,
  notifications,
  notificationsOpen,
  unreadNotificationCount,
  onDismissNotification,
  onLogoClick,
  onNotificationAction,
  onOpenGuide,
  onLogout,
  onNavChange,
  onAvailabilityChange,
  onToggleNotifications,
  availabilitySaving = false,
  roleScope,
}) {
  const notificationItems = notifications || [];
  const notificationRef = useRef(null);
  const userChipRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const isDealer = currentUser.role === "dealer";
  const agentStatusValue = currentUser.availability_status === "break"
    ? (currentUser.break_reason || "")
    : "online";

  useEffect(() => {
    if (!notificationsOpen) return;
    function handleClickOutside(event) {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        onToggleNotifications();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notificationsOpen, onToggleNotifications]);

  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClickOutside(event) {
      if (userChipRef.current && !userChipRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [userMenuOpen]);

  return (
    <header className="topbar">
      <div className="brand-block">
        <button
          type="button"
          className="brand-logo-button"
          onClick={onLogoClick}
          aria-label="Go to My Leads"
          title="My Leads"
        >
          <IndofastLogo height={28} variant="light" />
        </button>
        <div className="brand-divider" />
        <p className="topbar-scope-label">{roleScope?.label || "CRM Workspace"}</p>
      </div>

      <nav className="top-nav">
        {navItems.map((item) => {
          const Icon = ICONS[item.icon];
          return (
            <button
              type="button"
              key={item.label}
              className={`top-nav-item ${activeTopNav === item.label ? "active" : ""}`}
              onClick={() => onNavChange(item.label)}
            >
              <Icon />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="top-actions">
        <button className="icon-button" type="button" aria-label="Workflow guide" onClick={onOpenGuide}>
          <InfoIcon />
        </button>
        <div ref={notificationRef} style={{ position: "relative" }}>
        <button
          className="icon-button notification-button"
          type="button"
          aria-label="Notifications"
          aria-expanded={notificationsOpen}
          onClick={onToggleNotifications}
        >
          <BellIcon />
          {unreadNotificationCount > 0 ? (
            <span className="notification-badge">{unreadNotificationCount}</span>
          ) : null}
        </button>
        {notificationsOpen ? (
          <section className="notification-panel" aria-label="Notifications panel">
            <div className="notification-panel-head">
              <div>
                <strong>Notifications</strong>
                <p>Follow-ups, import issues, and sync alerts.</p>
              </div>
            </div>

            {notificationItems.length ? (
              <div className="notification-list">
                {notificationItems.map((notification) => (
                  <article
                    key={notification.id}
                    className={`notification-card ${notification.tone || "info"}`}
                  >
                    <div className="notification-card-copy">
                      <span className="notification-label">
                        {(notification.category || "info").replace(/_/g, " ")}
                      </span>
                      <strong>{notification.title}</strong>
                      <p>{notification.description}</p>
                      {notification.meta ? <small>{notification.meta}</small> : null}
                    </div>
                    <div className="notification-card-actions">
                      <button
                        type="button"
                        className="notification-action"
                        onClick={() => onNotificationAction(notification)}
                      >
                        {notification.actionLabel || "Open"}
                      </button>
                      <button
                        type="button"
                        className="notification-dismiss"
                        aria-label="Dismiss notification"
                        onClick={(e) => { e.stopPropagation(); onDismissNotification(notification.id); }}
                      >
                        ✕
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="notification-empty">
                No urgent notifications right now.
              </div>
            )}
          </section>
        ) : null}
        </div>
        {currentUser.role === "agent" ? (
          <label className="agent-status-control" title="Set availability">
            <span>Status</span>
            <select
              value={agentStatusValue}
              disabled={availabilitySaving}
              onChange={(event) => onAvailabilityChange?.(event.target.value)}
            >
              {AGENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        ) : null}
        <div
          ref={userChipRef}
          className="user-chip"
          style={isDealer ? { position: "relative", cursor: "pointer" } : undefined}
          onClick={isDealer ? () => setUserMenuOpen((o) => !o) : undefined}
        >
          <span className="avatar-badge">
            {currentUser.full_name
              .split(" ")
              .map((part) => part[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </span>
          <div className="user-chip-text" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: '1.2' }}>
            <span>{currentUser.full_name}</span>
            {!isDealer && (currentUser.primary_region_name || currentUser.primary_city_name) ? (
              <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                {currentUser.primary_city_name || "Any City"} / {currentUser.primary_region_name || "Any Region"}
              </span>
            ) : null}
          </div>

          {isDealer && userMenuOpen && (
            <div className="dealer-user-popover">
              <div className="dealer-user-popover-head">
                <span className="avatar-badge">
                  {currentUser.full_name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
                </span>
                <div>
                  <p className="dealer-user-popover-name">{currentUser.dealer_name || currentUser.full_name}</p>
                  <p className="dealer-user-popover-role">Dealer</p>
                </div>
              </div>
              <div className="dealer-user-popover-rows">
                <div className="dealer-user-popover-row">
                  <span>Username</span>
                  <strong>{currentUser.username || "—"}</strong>
                </div>
                <div className="dealer-user-popover-row">
                  <span>OEM</span>
                  <strong>{currentUser.dealer_oem || "—"}</strong>
                </div>
                <div className="dealer-user-popover-row">
                  <span>City</span>
                  <strong>{currentUser.dealer_city || "—"}</strong>
                </div>
              </div>
            </div>
          )}
        </div>
        <button type="button" className="logout-button" onClick={onLogout}>
          Logout
        </button>
      </div>
    </header>
  );
}

export default Topbar;
